((root, factory) => {
  const config = factory();
  if (typeof module === 'object' && module.exports) module.exports = config;
  root.CorkopiaBuildAdvisorConfig = config;
})(typeof globalThis !== 'undefined' ? globalThis : window, () => {
  'use strict';

  const config = {
    contractVersion: '1.0',
    advisorVersion: 'build-advisor-v1.0.0',
    ruleSetId: 'build_advisor_v1',
    ruleSetVersion: '1.0.0',
    rankingProfileId: 'build_advisor_deterministic_v1',
    rankingProfileVersion: '1.0.0',

    functions: {
      primary_hide: {
        label: 'Primary Hide',
        group: 'shelter',
        publicRoles: ['hide'],
        familyFallbacks: ['cork_round', 'half_round'],
        explanation: 'Creates a defined sheltered form in the composition.'
      },
      secondary_hide: {
        label: 'Secondary Hide',
        group: 'shelter',
        publicRoles: ['hide'],
        familyFallbacks: ['half_round', 'cork_round'],
        explanation: 'Adds a second sheltered form without duplicating the exact piece.'
      },
      vertical_structure: {
        label: 'Vertical Structure',
        group: 'structure',
        publicRoles: ['climb', 'bridge'],
        familyFallbacks: ['branch', 'statement_piece'],
        explanation: 'Introduces a strong upright line to the visual composition.'
      },
      background_coverage: {
        label: 'Background Coverage',
        group: 'coverage',
        publicRoles: ['background'],
        familyFallbacks: ['background_piece', 'cork_flat'],
        explanation: 'Builds a layered backdrop using a currently available exact piece.'
      },
      climbing_feature: {
        label: 'Climbing Feature',
        group: 'structure',
        publicRoles: ['climb', 'bridge'],
        familyFallbacks: ['branch', 'half_round'],
        explanation: 'Adds an elevated line or crossing form to the composition.'
      },
      display_piece: {
        label: 'Display Piece',
        group: 'focal',
        publicRoles: ['focal'],
        familyFallbacks: ['statement_piece', 'cork_round'],
        explanation: 'Gives the build one visually dominant natural form.'
      },
      orchid_mount: {
        label: 'Orchid Mount',
        group: 'mount',
        publicRoles: ['mount'],
        familyFallbacks: ['cork_flat', 'branch'],
        explanation: 'Provides a natural mounting surface; plant suitability is not inferred.'
      },
      accent_piece: {
        label: 'Accent Piece',
        group: 'accent',
        publicRoles: ['focal', 'bridge'],
        familyFallbacks: ['half_round', 'branch', 'cork_flat'],
        explanation: 'Adds a smaller contrasting form to finish the visual direction.'
      }
    },

    styleProfiles: {
      dense_jungle: {
        nameToken: 'Rainforest',
        functions: ['background_coverage', 'vertical_structure', 'primary_hide', 'accent_piece'],
        families: ['background_piece', 'branch', 'cork_round', 'half_round']
      },
      natural_forest: {
        nameToken: 'Forest',
        functions: ['primary_hide', 'vertical_structure', 'secondary_hide', 'accent_piece'],
        families: ['cork_round', 'branch', 'half_round', 'cork_flat']
      },
      open_climbing: {
        nameToken: 'Open',
        functions: ['climbing_feature', 'vertical_structure', 'display_piece', 'accent_piece'],
        families: ['branch', 'statement_piece', 'half_round']
      },
      heavy_background: {
        nameToken: 'Layered',
        functions: ['background_coverage', 'vertical_structure', 'primary_hide', 'accent_piece'],
        families: ['background_piece', 'cork_flat', 'branch']
      },
      display_piece: {
        nameToken: 'Sculptural',
        functions: ['display_piece', 'accent_piece', 'background_coverage'],
        families: ['statement_piece', 'cork_round', 'background_piece']
      },
      minimal: {
        nameToken: 'Quiet',
        functions: ['primary_hide', 'accent_piece', 'vertical_structure'],
        families: ['half_round', 'cork_flat', 'branch']
      },
      mixed: {
        nameToken: 'Natural',
        functions: ['primary_hide', 'vertical_structure', 'background_coverage', 'accent_piece'],
        families: ['cork_round', 'branch', 'background_piece', 'half_round', 'cork_flat']
      },
      surprise_me: {
        nameToken: 'Wild',
        functions: ['display_piece', 'vertical_structure', 'primary_hide', 'accent_piece'],
        families: ['statement_piece', 'branch', 'cork_round', 'half_round']
      }
    },

    preferenceProfiles: {
      mixed_cork: {
        label: 'Mixed Cork',
        families: ['cork_round', 'branch', 'half_round', 'cork_flat', 'background_piece', 'statement_piece']
      },
      hollow_forms: {
        label: 'Hollow Forms',
        families: ['cork_round', 'half_round']
      },
      flat_layers: {
        label: 'Flat Layers',
        families: ['cork_flat', 'background_piece']
      },
      branching_forms: {
        label: 'Branching Forms',
        families: ['branch']
      },
      statement_forms: {
        label: 'Statement Forms',
        families: ['statement_piece', 'cork_round']
      },
      no_preference: {
        label: 'No Preference',
        families: []
      }
    },

    moreLikeProfiles: {
      more_hollow: {
        label: 'More Hollow',
        functionId: 'primary_hide',
        families: ['cork_round', 'half_round'],
        publicRoles: ['hide'],
        requiresOpening: true
      },
      more_vertical: {
        label: 'More Vertical',
        functionId: 'vertical_structure',
        families: ['branch', 'statement_piece'],
        publicRoles: ['climb', 'bridge'],
        requiresOpening: false
      },
      more_background: {
        label: 'More Background',
        functionId: 'background_coverage',
        families: ['background_piece', 'cork_flat'],
        publicRoles: ['background'],
        requiresOpening: false
      },
      more_dramatic: {
        label: 'More Dramatic',
        functionId: 'display_piece',
        families: ['statement_piece', 'cork_round'],
        publicRoles: ['focal'],
        requiresOpening: false
      },
      more_minimal: {
        label: 'More Minimal',
        functionId: 'accent_piece',
        families: ['half_round', 'cork_flat'],
        publicRoles: [],
        requiresOpening: false
      },
      more_natural: {
        label: 'More Natural',
        functionId: 'accent_piece',
        families: ['branch', 'cork_round', 'half_round', 'cork_flat'],
        publicRoles: [],
        requiresOpening: false
      }
    },

    scoring: {
      signals: {
        function_match: { enabled: true, weight: 28 },
        budget_match: { enabled: true, weight: 20 },
        style_match: { enabled: true, weight: 14 },
        preference_match: { enabled: true, weight: 10 },
        family_variety: { enabled: true, weight: 8 },
        emphasis_match: { enabled: true, weight: 10 },
        merchandising_priority: { enabled: true, weight: 5 },
        recent_publication: { enabled: true, weight: 5 },
        size_match: { enabled: false, weight: 0 },
        enclosure_match: { enabled: false, weight: 0 },
        animal_compatibility: { enabled: false, weight: 0 },
        inventory_priority: { enabled: false, weight: 0 },
        wave_priority: { enabled: false, weight: 0 }
      },
      values: {
        exactPublicRole: 1,
        familyRoleFallback: 0.72,
        preferredFamily: 1,
        representedFamily: 0,
        newFamily: 1,
        explicitMerchandisingLabel: 1,
        newArrival: 1,
        noEmphasisRequested: 0.5,
        emphasisFamily: 0.8,
        emphasisRole: 1,
        requiredOpeningPresent: 1,
        requiredOpeningMissing: 0
      },
      confidenceBands: {
        highMinimum: 0.78,
        mediumMinimum: 0.55
      },
      maximumAlternatives: 3
    },

    naming: {
      projectTokens: {
        arboreal_fixture: 'Canopy',
        terrestrial_fixture: 'Woodland',
        plant_fixture: 'Living',
        crested_gecko: 'Canopy',
        ball_python: 'Woodland',
        bearded_dragon: 'Sunlit',
        dart_frogs: 'Moss',
        orchid_mount: 'Orchid',
        bioactive_display: 'Living',
        tarantula: 'Earthen',
        isopods: 'Forest-Floor',
        other: 'Natural'
      },
      structureTokens: {
        vertical: 'Ascent',
        horizontal: 'Hideaway',
        balanced: 'Escape'
      },
      orientationRatios: {
        verticalMinimum: 1.2,
        horizontalMinimum: 1.35
      }
    },

    buildScore: {
      categoryLabels: {
        starting: 'Starting',
        developing: 'Developing',
        considered: 'Considered'
      },
      thresholds: {
        developingCount: 1,
        consideredCount: 2,
        varietyDeveloping: 2,
        varietyConsidered: 3,
        budgetComfortMaximum: 0.8,
        budgetNearLimitMaximum: 1
      }
    },

    limits: {
      minimumDimensionIn: 4,
      maximumDimensionIn: 120,
      maximumDecisionHistory: 200,
      maximumShownHistory: 200
    },

    authority: {
      currentCatalogProvider: 'shopify_collection',
      compatibilityApproved: false,
      enclosureFitApproved: false,
      sizeTaxonomyApproved: false,
      c3Connected: false,
      packingImplementedByAdvisor: false
    }
  };

  const deepFreeze = (value) => {
    Object.freeze(value);
    Object.values(value).forEach((child) => {
      if (child && typeof child === 'object' && !Object.isFrozen(child)) deepFreeze(child);
    });
    return value;
  };

  return deepFreeze(config);
});
